import { BrowserPhoneState } from "./telephonyTypes";
import { isDesktopApp, shouldUseNativeSip } from "../../../utils/isDesktop";
import {
  isLinkusProvider,
  isWebSocketConnectingError,
  normalizeTelephonyError,
} from "./linkusSdkResult";

const NETWORK_ERROR_PATTERNS = [
  "cannot connect",
  "websocket",
  "web socket",
  "wss://",
  "ws://",
  "network",
  "timed out",
  "timeout",
  "connection refused",
  "err_connection",
  "failed to fetch",
  "disconnected",
  "transport",
  "econnrefused",
  "enetunreach",
  "networkerror",
  "192.168.",
  "10.",
  "172.16.",
  "172.17.",
  "172.18.",
  "172.19.",
  "172.2",
  "172.30.",
  "172.31.",
];

const AUTH_ERROR_PATTERNS = [
  "401",
  "403",
  "unauthorized",
  "sip password",
  "sip registration failed",
];

const EXTENSION_SETUP_PATTERNS = [
  "sign/create",
  "errcode=40002",
  "parameter error",
  "agent email or extension is required",
  "extension is required",
  "user not found",
  "username not found",
  "invalid username",
  "invalid extension",
  "extension does not exist",
  "extension not found",
];

const TECHNICAL_ERROR_PATTERNS = [
  "errcode=",
  "errmsg=",
  "yeastar ",
  "details:",
];

export const isTelephonyNetworkError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return NETWORK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const isTelephonyAuthError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return AUTH_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const isExtensionMissingError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return (
    message.includes("agent email or extension is required") ||
    message.includes("extension is required")
  );
};

export const isExtensionSetupError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return EXTENSION_SETUP_PATTERNS.some((pattern) => message.includes(pattern));
};

export const isTechnicalTelephonyError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Maps SIP / browser-phone errors to user-facing messages (i18n via t()).
 */
export const getTelephonyErrorMessage = (error, t, options = {}) => {
  const normalized = normalizeTelephonyError(error);
  if (!normalized) {
    return t("ccToastBrowserPhoneConnecting");
  }
  const message = normalized.message.trim();
  const providerName = options.providerName;

  if (
    isDesktopApp() &&
    /bridge is not available|sip engine is not available/i.test(message)
  ) {
    return message;
  }

  if (isTelephonyNetworkError(normalized)) {
    if (shouldUseNativeSip(providerName)) {
      return t("ccToastNativeSipRegistrationFailed");
    }
    if (isLinkusProvider(providerName)) {
      if (isWebSocketConnectingError(normalized)) {
        return t("ccToastBrowserPhoneConnecting");
      }
      return t("ccBrowserPhoneLinkusConnectionFailed");
    }
    return t("ccToastBrowserPhoneOfficeNetworkRequired");
  }
  if (
    shouldUseNativeSip(providerName) &&
    /registration timed out|sip registration failed|registration failed|forbidden|unauthorized|403|401/i.test(
      message
    )
  ) {
    const detail = message.replace(/^SIP registration failed[:\s]*/i, "").trim();
    const base = t("ccToastNativeSipRegistrationFailed");
    if (detail && detail.length < 120 && !/^sip registration failed$/i.test(detail)) {
      return `${base} (${detail})`;
    }
    return base;
  }
  if (isTelephonyAuthError(normalized)) {
    return t("ccToastBrowserPhoneAuthFailed");
  }

  if (
    message.includes("ACCOUNT IP BLOCKED") ||
    message.includes("errcode=70004") ||
    message.includes("blocked this browser IP") ||
    message.includes("blocked this server's IP")
  ) {
    return t("ccBrowserPhonePbxIpBlocked");
  }
  if (
    message.includes("PKIX") ||
    message.includes("certificate_unknown") ||
    message.includes("certification path") ||
    message.includes("HTTPS certificate is not trusted")
  ) {
    return t("ccBrowserPhonePbxSslError");
  }
  if (
    message.includes("rejected the Linkus SDK Access ID/Key") ||
    message.includes("username or password")
  ) {
    return t("ccBrowserPhonePbxCredentialsRejected");
  }
  if (
    message.includes("D2C Linkus SDK credentials are missing") ||
    message.includes("D2C Cloud PBX is not configured")
  ) {
    return t("ccBrowserPhoneSdkNotConfigured");
  }
  if (message.includes("allow CORS")) {
    return message;
  }
  if (message.toLowerCase().includes("not connected") || message.toLowerCase().includes("not enabled")) {
    return t("ccToastBrowserPhoneNotReady");
  }
  if (isExtensionMissingError(normalized)) {
    return t("ccBrowserPhoneExtensionMissing");
  }
  if (isExtensionSetupError(normalized)) {
    return t("ccBrowserPhoneExtensionMismatch");
  }
  if (isTechnicalTelephonyError(normalized)) {
    return t("ccBrowserPhoneUnexpectedError");
  }

  return message;
};

/**
 * Resolves the browser-phone panel error for display (status bar, dialer).
 */
export const getBrowserPhonePanelError = (store, t, options = {}) => {
  if (!store?.error) {
    return null;
  }

  const showErrorStates = new Set([
    BrowserPhoneState.REGISTRATION_FAILED,
    BrowserPhoneState.CONNECTING,
    BrowserPhoneState.DISCONNECTED,
  ]);

  if (!showErrorStates.has(store.state)) {
    return null;
  }

  return getTelephonyErrorMessage({ message: store.error }, t, options);
};

export const isBrowserPhoneUnavailable = (store) =>
  store?.state === BrowserPhoneState.REGISTRATION_FAILED ||
  (Boolean(store?.error) &&
    store.state !== BrowserPhoneState.REGISTERED &&
    ![
      BrowserPhoneState.INCOMING_CALL,
      BrowserPhoneState.INCOMING_CONNECTING,
      BrowserPhoneState.OUTGOING_CALL,
      BrowserPhoneState.ACTIVE_CALL,
      BrowserPhoneState.HOLD,
    ].includes(store.state));
